import moment from 'moment';

import {
    Address,
    IAddress,
    ShipmentStatus,
    IVoucher,
    IPrice,
    ShipmentThirdPartyPreferencesProps,
} from '@packlink/packlink-sdk';

import {
    CheckoutAction,
    REMOVE_VOUCHER,
    SET_CARRIERS_TERMS_AND_CONDITIONS,
    SET_CLIENT_INPUT_MODE,
    SET_COLLECTION,
    SET_COLLECTION_DATE,
    SET_COLLECTION_INTERVAL,
    SET_DROPOFF_ID,
    SET_FROM,
    SET_IN_POST_LOCKERS,
    SET_LABEL,
    SET_PACKAGES,
    SET_PRICE_DETAILS,
    SET_SERVICE,
    SET_SERVICES_TAB,
    SET_TO,
    SET_VOUCHER,
    UPDATE_SHIPMENT,
} from '@store/actions';

import {
    ICollectionState,
    IContent,
    IParcel,
    IQR,
    IServiceState,
    IShipment,
    IShipmentItem,
    NotEbayItemParcel,
    NotSetParcel,
} from '@types';

import { getEbayItemPackageDimensions } from '@utils';

export enum ServicesType {
    DropOff = 'DropOff',
    Collection = 'Collection',
}

export enum ClientInputMode {
    NOT_SET = 'not-set',
    CHIPS = 'chips',
    CHIPS_AND_FIELDS = 'chips-and-fields',
    FIELDS = 'fields',
}

export interface ICheckoutState {
    carriersTermsAndConditions?: boolean;
    clientInputMode: ClientInputMode;
    collection: ICollectionState | null;
    content: IContent | null;
    currency: string | null;
    customReference: string | null; // eBay exclusive
    from: IAddress | null;
    items: IShipmentItem[]; // eBay exclusive
    label?: string;
    packages?: IParcel;
    priceDetails?: IPrice;
    qr: IQR | null;
    reference: string | null;
    service: IServiceState | null;
    shippingServiceCost: number; // eBay exclusive
    shippingServiceCurrency: string | undefined;
    shippingServiceName: string | null; // eBay exclusive
    shipment: IShipment | null;
    state: ShipmentStatus;
    to: IAddress | null;
    trackings: string[];
    defaultParcelDimensions: IParcel;
    dropoffPointId: string | null;
    orderDate: string;
    orderTotal: number;
    servicesTabSelected: ServicesType;
    selectedPickUpPointId: string | null;
    selectedServiceId: number | null;
    voucher: IVoucher | null;
    thirdPartyPreferences?: ShipmentThirdPartyPreferencesProps;
}

export const initialState: ICheckoutState = {
    carriersTermsAndConditions: false,
    clientInputMode: ClientInputMode.NOT_SET,
    collection: null,
    content: null,
    currency: null,
    customReference: null, // eBay exclusive
    defaultParcelDimensions: NotEbayItemParcel,
    dropoffPointId: null,
    from: null,
    items: [], // eBay exclusive
    label: undefined,
    packages: NotSetParcel,
    priceDetails: undefined,
    qr: null,
    reference: null,
    service: null,
    shipment: null,
    shippingServiceCost: 0, // eBay exclusive
    shippingServiceCurrency: undefined,
    shippingServiceName: null, // eBay exclusive
    state: ShipmentStatus.AWAITING_COMPLETION,
    to: null,
    trackings: [],
    orderDate: '',
    orderTotal: 0,
    servicesTabSelected: ServicesType.DropOff,
    selectedPickUpPointId: null,
    selectedServiceId: null,
    voucher: null,
    thirdPartyPreferences: undefined,
};

export const reducer = (state: ICheckoutState = initialState, action: CheckoutAction): ICheckoutState => {
    const { type, payload } = action;

    switch (type) {
        case SET_CARRIERS_TERMS_AND_CONDITIONS: {
            const carriersTermsAndConditions = payload as boolean;
            return { ...state, carriersTermsAndConditions };
        }
        case UPDATE_SHIPMENT: {
            const shipment = payload as IShipment;
            const additionalData = shipment.additional_data || {};

            return {
                ...state,
                content: {
                    type: shipment.content ? shipment.content : '',
                    value: shipment.contentvalue ? shipment.contentvalue : 0,
                },
                currency: shipment.currency,
                customReference: shipment.shipment_custom_reference || null, // eBay exclusive
                defaultParcelDimensions: getEbayItemPackageDimensions(shipment),
                dropoffPointId: shipment.dropoff_point_id || null,
                items: additionalData.items || [], // eBay exclusive
                label: shipment?.labels?.[0],
                qr:
                    shipment.print_in_store_selected && shipment.print_in_store
                        ? {
                              reference: shipment.print_in_store.qr_reference,
                              url: shipment.print_in_store.qr_url,
                          }
                        : null,
                reference: shipment.packlink_reference,
                shippingServiceCost: additionalData.shipping_service_cost || 0, // eBay exclusive
                shippingServiceCurrency: additionalData.shipping_service_cost_currency || shipment.currency,
                shippingServiceName: additionalData.shipping_service_name || null, // eBay exclusive
                state: shipment.state,
                trackings: shipment.trackings || [],
                orderDate: shipment.order_date || '',
                orderTotal: shipment.contentvalue || 0,
                selectedPickUpPointId: shipment.third_party_preferences?.drop_off_point_id || null,
                selectedServiceId: shipment.third_party_preferences?.service_id
                    ? parseInt(shipment.third_party_preferences.service_id)
                    : null,
                thirdPartyPreferences: shipment?.third_party_preferences
                    ? {
                          carrier: shipment.third_party_preferences.carrier,
                          serviceId: shipment.third_party_preferences.service_id,
                          dropoffPointId: shipment.third_party_preferences.drop_off_point_id,
                      }
                    : undefined,
            };
        }
        case SET_SERVICE: {
            const service = payload as IServiceState;

            return { ...state, service };
        }
        case SET_FROM: {
            const from = payload as Address;
            return { ...state, from: from.toJSON() as IAddress };
        }
        case SET_TO: {
            const to = payload as Address;
            return { ...state, to: to.toJSON() as IAddress };
        }
        case SET_PRICE_DETAILS: {
            const priceDetails = payload as IPrice;
            return { ...state, priceDetails };
        }
        case SET_PACKAGES: {
            const packages = payload as IParcel;
            return { ...state, packages };
        }
        case SET_IN_POST_LOCKERS: {
            if (!state.service) {
                return { ...state };
            }
            return {
                ...state,
                service: {
                    ...state.service,
                    inPostServiceSelected: payload as boolean,
                },
            };
        }
        case SET_LABEL: {
            const label = action.payload as string;
            return { ...state, label: label };
        }
        case SET_COLLECTION: {
            const collection = payload as ICollectionState;

            if (!collection) {
                return { ...state, collection };
            }

            return { ...state, collection: { ...state.collection, ...collection } };
        }
        case SET_COLLECTION_DATE: {
            const date = payload as moment.Moment;
            const collection = { ...state.collection, date } as ICollectionState;
            return { ...state, collection };
        }
        case SET_COLLECTION_INTERVAL: {
            const interval = payload as string;
            const collection = { ...state.collection, interval } as ICollectionState;
            return { ...state, collection };
        }

        case REMOVE_VOUCHER: {
            return {
                ...state,
                voucher: null,
            };
        }
        case SET_CLIENT_INPUT_MODE: {
            return {
                ...state,
                clientInputMode: payload as ClientInputMode,
            };
        }
        case SET_VOUCHER: {
            const voucher = payload as IVoucher;
            return {
                ...state,
                voucher: voucher,
            };
        }
        case SET_DROPOFF_ID: {
            return {
                ...state,
                dropoffPointId: payload as string | null,
            };
        }
        case SET_SERVICES_TAB: {
            return { ...state, servicesTabSelected: payload as ServicesType };
        }
        default: {
            return state;
        }
    }
};
